import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const TOUR_KEY = "ndis_welcome_tour_seen";

const STEPS = [
  {
    id: "email",
    selector: '[data-testid="setup-step-email"]',
    title: "Step 1 of 4 — Connect your email",
    body: "First up: connect your own email account so invoices and incident alerts can be sent automatically. Open Settings → Email when you're ready, or follow our 2-minute Gmail walkthrough.",
  },
  {
    id: "budget",
    selector: '[data-testid="setup-step-budget"]',
    title: "Step 2 of 4 — Set your NDIS budget",
    body: "Enter your plan total and start/end dates from your NDIS plan letter. The dashboard will show a live burn-rate projection so you never overspend.",
  },
  {
    id: "worker",
    selector: '[data-testid="setup-step-worker"]',
    title: "Step 3 of 4 — Add a support worker",
    body: "Capture each worker's name, ABN, bank details and pay rates. These flow into every timesheet and invoice automatically.",
  },
  {
    id: "register",
    selector: '[data-testid="setup-step-register"]',
    title: "Step 4 of 4 — Invite them to register",
    body: "Share the app URL — workers sign up using the same email you added them with and they'll instantly see your account on their side. That's it!",
  },
];

const PADDING = 8;     // visual padding around the highlighted element
const TIP_GAP = 14;    // vertical gap between target and tooltip card
const TIP_WIDTH = 360;

export default function WelcomeTour({ open, onClose }) {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);

  const measure = useCallback(() => {
    const step = STEPS[index];
    if (!step) return;
    const el = document.querySelector(step.selector);
    if (!el) {
      // Target missing (e.g., SetupChecklist dismissed or completed) — bail out gracefully.
      onClose?.();
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    // Wait for scroll to settle, then re-measure.
    setTimeout(() => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    }, 360);
  }, [index, onClose]);

  useEffect(() => {
    if (!open) return;
    measure();
    const handler = () => measure();
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [open, measure]);

  if (!open || !rect) return null;

  const step = STEPS[index];
  const isLast = index === STEPS.length - 1;

  // Choose the side that has more room (tooltip below by default, above if not enough space)
  const viewportH = window.innerHeight;
  const placeBelow = (rect.top + rect.height + TIP_GAP + 200) < viewportH;
  const tipTop = placeBelow
    ? rect.top + rect.height + TIP_GAP
    : Math.max(16, rect.top - TIP_GAP - 200);
  // Centre tooltip horizontally above/below the target, clamped to viewport.
  const targetCentre = rect.left + rect.width / 2;
  let tipLeft = targetCentre - TIP_WIDTH / 2;
  tipLeft = Math.max(16, Math.min(window.innerWidth - TIP_WIDTH - 16, tipLeft));

  const finish = () => {
    localStorage.setItem(TOUR_KEY, "1");
    onClose?.();
  };

  return createPortal(
    <div className="fixed inset-0 z-[1000]" data-testid="welcome-tour" role="dialog" aria-modal="true">
      {/* Dark backdrop with a transparent cutout around the target */}
      <svg className="absolute inset-0 w-full h-full pointer-events-auto" onClick={(e) => e.target === e.currentTarget && null /* swallow clicks on backdrop */}>
        <defs>
          <mask id="welcome-tour-mask">
            <rect width="100%" height="100%" fill="white" />
            <rect
              x={rect.left - PADDING} y={rect.top - PADDING}
              width={rect.width + PADDING * 2} height={rect.height + PADDING * 2}
              rx={20} ry={20} fill="black"
            />
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(15, 23, 42, 0.62)" mask="url(#welcome-tour-mask)" />
      </svg>

      {/* Glowing ring around the target */}
      <div
        className="absolute pointer-events-none rounded-2xl ring-4 ring-white/80 shadow-[0_0_0_8px_rgba(93,211,176,0.35),_0_0_40px_8px_rgba(93,211,176,0.5)] transition-all duration-300"
        style={{
          top: rect.top - PADDING, left: rect.left - PADDING,
          width: rect.width + PADDING * 2, height: rect.height + PADDING * 2,
        }}
      />

      {/* Tooltip card */}
      <div
        className="absolute bg-card text-card-foreground rounded-3xl border-2 border-client/40 shadow-2xl p-5 w-[360px]"
        style={{ top: tipTop, left: tipLeft }}
        data-testid="welcome-tour-tooltip"
      >
        <button
          onClick={finish}
          aria-label="Skip tour"
          data-testid="welcome-tour-skip"
          className="absolute top-3 right-3 w-7 h-7 rounded-full grid place-items-center text-muted-foreground hover:bg-muted/60 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-xl bg-client/15 ring-1 ring-client/25 grid place-items-center">
            <Sparkles className="w-4 h-4 text-client" />
          </div>
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-client">Quick tour</p>
        </div>
        <h3 className="text-lg font-display font-black tracking-tight leading-tight" data-testid="welcome-tour-title">
          {step.title}
        </h3>
        <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{step.body}</p>

        {/* Progress dots */}
        <div className="flex gap-1.5 mt-4" data-testid="welcome-tour-dots">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? "w-6 bg-client" : i < index ? "w-1.5 bg-client/50" : "w-1.5 bg-muted-foreground/25"
              }`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between mt-4 gap-2">
          <Button
            variant="ghost" size="sm"
            onClick={finish}
            data-testid="welcome-tour-skip-text"
            className="text-xs text-muted-foreground rounded-full"
          >
            Skip tour
          </Button>
          <div className="flex gap-1.5">
            {index > 0 && (
              <Button
                variant="outline" size="sm"
                onClick={() => setIndex(index - 1)}
                data-testid="welcome-tour-back"
                className="rounded-full font-bold gap-1 border-2"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => (isLast ? finish() : setIndex(index + 1))}
              data-testid={isLast ? "welcome-tour-done" : "welcome-tour-next"}
              className="rounded-full font-bold gap-1 text-white"
              style={{ background: "hsl(var(--client))" }}
            >
              {isLast ? "Got it!" : "Next"}
              {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Helper for the consumer (SetupChecklistCard) to decide whether to autostart.
export function shouldAutoStartTour() {
  return localStorage.getItem(TOUR_KEY) !== "1";
}

export function markTourSeen() {
  localStorage.setItem(TOUR_KEY, "1");
}
